/**
 * LeaveInsights.js
 *
 * Leave Insights tab for the RO Team Dashboard.
 *
 * Three sections:
 *   A. Leave Health Summary — team-level metrics cards
 *   B. Individual Leave Comparison — horizontal stacked bars
 *   C. Patterns & Flags — only shown when meaningful
 *
 * Employee theme (Navy Blue). Strict design-system-reference.md adherence.
 */

import React, { useMemo } from 'react';
import {
  AlertTriangle, Calendar, Heart, Thermometer,
  Clock, CalendarOff, Home, ExternalLink,
} from 'lucide-react';
import { ClassificationBadge } from '../UI/ClassificationBadge';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, Legend,
} from 'recharts';

// ---------------------------------------------------------------------------
// Section A: Team-level Summary Cards (metrics card pattern)
// ---------------------------------------------------------------------------

function TeamSummaryCards({ summary, year }) {
  if (!summary) return null;

  const cards = [
    {
      icon: Calendar,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-800',
      value: summary.avg_al_utilisation != null
        ? `${Math.round(summary.avg_al_utilisation * 100)}%`
        : '—',
      label: 'Team Avg AL Used',
      sub: `${year} year-to-date`,
    },
    {
      icon: Thermometer,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      value: summary.avg_mc_days ?? 0,
      label: 'Avg MC Days / Person',
      sub: `${year} year-to-date`,
    },
    {
      icon: CalendarOff,
      iconBg: summary.zero_al_taken > 0 ? 'bg-red-100' : 'bg-green-100',
      iconColor: summary.zero_al_taken > 0 ? 'text-red-600' : 'text-green-600',
      value: summary.zero_al_taken,
      label: 'Zero AL Taken',
      sub: summary.zero_al_taken > 0 ? 'May need a wellbeing check' : 'All clear',
    },
    {
      icon: AlertTriangle,
      iconBg: summary.low_al_remaining > 0 ? 'bg-amber-100' : 'bg-green-100',
      iconColor: summary.low_al_remaining > 0 ? 'text-amber-600' : 'text-green-600',
      value: summary.low_al_remaining,
      label: '< 3 Days AL Left',
      sub: 'Limited leave buffer',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.label} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-start">
              <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${c.iconBg}`}>
                <Icon className={`w-4 h-4 sm:w-6 sm:h-6 ${c.iconColor}`} />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{c.value}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">{c.label}</p>
                {c.sub && (
                  <p className="text-[11px] sm:text-xs text-gray-500 mt-1 truncate">{c.sub}</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section B: Individual Leave Comparison (horizontal stacked bar chart)
// ---------------------------------------------------------------------------

function LeaveComparisonChart({ members }) {
  const chartData = useMemo(() => {
    if (!members?.length) return [];
    return members
      .map((m) => ({
        name: m.full_name,
        'AL Used': m.leave_balances?.find((b) => b.leave_type === 'Annual Leave')?.used ?? 0,
        'AL Remaining': m.al_remaining_days != null ? Math.max(0, m.al_remaining_days) : 0,
        'MC Days': m.mc_days_ytd ?? 0,
      }))
      .sort((a, b) => a['AL Remaining'] - b['AL Remaining']);
  }, [members]);

  if (!chartData.length) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
          <Calendar className="w-5 h-5 text-blue-800" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-800">Leave Usage Comparison</h3>
          <p className="text-xs text-gray-500">
            AL used vs remaining, sorted by least remaining first
          </p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 40 + 40)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10 }} unit=" days" />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 10 }}
            width={80}
            tickFormatter={(name) => name.length > 12 ? name.slice(0, 11) + '…' : name}
          />
          <Tooltip
            formatter={(v, name) => [`${v} days`, name]}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="AL Used" stackId="al" fill="#1e40af" radius={[0, 0, 0, 0]} />
          <Bar dataKey="AL Remaining" stackId="al" fill="#93c5fd" radius={[0, 4, 4, 0]} />
          <Bar dataKey="MC Days" fill="#f59e0b" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section B.2: Days Since Last Leave (recency indicator)
// ---------------------------------------------------------------------------

function LastLeaveSummary({ members }) {
  const sorted = useMemo(() => {
    if (!members?.length) return [];
    return [...members]
      .filter((m) => m.days_since_last_leave != null)
      .sort((a, b) => b.days_since_last_leave - a.days_since_last_leave);
  }, [members]);

  const neverTaken = members?.filter((m) => m.days_since_last_leave == null && m.last_leave_date == null) ?? [];

  if (!sorted.length && !neverTaken.length) return null;

  const getRecencyColor = (days) => {
    if (days >= 90) return 'bg-red-100 text-red-700 ring-1 ring-red-600/20';
    if (days >= 60) return 'bg-amber-100 text-amber-700 ring-1 ring-amber-600/20';
    if (days >= 30) return 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20';
    return 'bg-green-50 text-green-700 ring-1 ring-green-600/20';
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
          <Clock className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-800">Days Since Last Leave</h3>
          <p className="text-xs text-gray-500">
            Excludes WFH and External Activity — counts actual time off
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {sorted.map((m) => (
          <div key={m.staff_id} className="flex items-center justify-between p-3 border border-gray-200 rounded-xl">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium text-gray-900 truncate">{m.full_name}</span>
              <ClassificationBadge classification={m.classification} />
            </div>
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full flex-shrink-0 ${getRecencyColor(m.days_since_last_leave)}`}>
              {m.days_since_last_leave} days
            </span>
          </div>
        ))}
        {neverTaken.map((m) => (
          <div key={m.staff_id} className="flex items-center justify-between p-3 border border-gray-200 rounded-xl">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium text-gray-900 truncate">{m.full_name}</span>
              <ClassificationBadge classification={m.classification} />
            </div>
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 ring-1 ring-red-600/20 flex-shrink-0">
              No leave taken
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section C: Patterns & Flags
// ---------------------------------------------------------------------------

function PatternsAndFlags({ members }) {
  const flags = useMemo(() => {
    if (!members?.length) return [];
    const result = [];

    for (const m of members) {
      const memberFlags = [];

      if (m.mon_fri_mc_flag) {
        memberFlags.push({
          type: 'mc_pattern',
          severity: 'amber',
          label: `Mon/Fri MC pattern: ${Math.round((m.mon_fri_mc_rate ?? 0) * 100)}% of ${m.mc_days_ytd} MC days fall on Monday or Friday`,
        });
      }

      if (m.days_since_last_leave != null && m.days_since_last_leave >= 90) {
        memberFlags.push({
          type: 'no_recent_leave',
          severity: 'red',
          label: `${m.days_since_last_leave} days since last leave — potential burnout risk`,
        });
      } else if (m.days_since_last_leave == null && m.last_leave_date == null) {
        memberFlags.push({
          type: 'never_taken',
          severity: 'red',
          label: 'No leave taken this year',
        });
      }

      if (m.al_remaining_days != null && m.al_remaining_days < 3 && m.al_remaining_days >= 0) {
        memberFlags.push({
          type: 'low_al',
          severity: 'amber',
          label: `Only ${m.al_remaining_days} AL day${m.al_remaining_days !== 1 ? 's' : ''} remaining`,
        });
      }

      if (memberFlags.length > 0) {
        result.push({ ...m, flags: memberFlags });
      }
    }

    return result.sort((a, b) => {
      const aMax = a.flags.some((f) => f.severity === 'red') ? 1 : 0;
      const bMax = b.flags.some((f) => f.severity === 'red') ? 1 : 0;
      return bMax - aMax;
    });
  }, [members]);

  if (!flags.length) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
            <Heart className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-800">Patterns & Flags</h3>
            <p className="text-xs text-gray-500">Wellbeing signals</p>
          </div>
        </div>
        <p className="text-sm text-green-600 font-medium flex items-center gap-1.5 mt-2">
          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
          No concerning patterns detected across the team.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-800">Patterns & Flags</h3>
          <p className="text-xs text-gray-500">
            {flags.length} team member{flags.length !== 1 ? 's' : ''} with signals worth reviewing
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {flags.map((m) => (
          <div
            key={m.staff_id}
            className="rounded-xl border border-gray-100 p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">{m.full_name}</span>
              <ClassificationBadge classification={m.classification} />
            </div>
            <div className="space-y-1.5">
              {m.flags.map((f, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-2 text-xs px-3 py-2 rounded-lg ${
                    f.severity === 'red'
                      ? 'bg-red-50 text-red-700'
                      : 'bg-amber-50 text-amber-700'
                  }`}
                >
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-400 mt-4">
        Mon/Fri MC flag requires 4+ MC days and ≥50% falling on Monday or Friday.
        Leave recency excludes WFH and External Activity.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Work Arrangement Summary (WFH / EA counts)
// ---------------------------------------------------------------------------

function WorkArrangementOverview({ members }) {
  const data = useMemo(() => {
    if (!members?.length) return [];
    return [...members]
      .map((m) => ({
        name: m.full_name,
        WFH: m.wfh_days_ytd ?? 0,
        EA: m.ea_days_ytd ?? 0,
      }))
      .sort((a, b) => (b.WFH + b.EA) - (a.WFH + a.EA));
  }, [members]);

  const hasData = data.some((d) => d.WFH > 0 || d.EA > 0);
  if (!hasData) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center">
          <Home className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-800">Work Arrangements</h3>
          <p className="text-xs text-gray-500">WFH and External Activity days year-to-date</p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={Math.max(160, data.length * 36 + 40)}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10 }} unit=" days" />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 10 }}
            width={80}
            tickFormatter={(name) => name.length > 12 ? name.slice(0, 11) + '…' : name}
          />
          <Tooltip
            formatter={(v, name) => [`${v} days`, name]}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="WFH" stackId="arr" fill="#4338ca" radius={[0, 0, 0, 0]} />
          <Bar dataKey="EA" stackId="arr" fill="#0891b2" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export default function LeaveInsights({ data, isLoading, error }) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-48 rounded-2xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
        <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
        <p className="font-medium">Failed to load leave insights</p>
        <p className="text-sm mt-1">{error.response?.data?.detail || error.message}</p>
      </div>
    );
  }

  const members = data?.members ?? [];
  const summary = data?.team_summary;
  const year = data?.year;

  if (!members.length) {
    return <div className="text-center py-12 text-gray-400 text-sm">No leave data available.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Section A: Team-level summary */}
      <TeamSummaryCards summary={summary} year={year} />

      {summary?.year_end_al?.visible && (
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-blue-800" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-800">Year-end Annual Leave</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Team aggregate only — employees receive their own planning reminders.
              </p>
            </div>
          </div>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <div className="rounded-xl border border-gray-200 px-3 py-2">
              <dt className="text-[11px] text-gray-500">Confirmed at risk</dt>
              <dd className="text-lg font-semibold text-gray-900">
                {summary.year_end_al.confirmed_at_risk_count ?? 0}
              </dd>
            </div>
            <div className="rounded-xl border border-gray-200 px-3 py-2">
              <dt className="text-[11px] text-gray-500">Safe if leave is approved</dt>
              <dd className="text-lg font-semibold text-gray-900">
                {summary.year_end_al.pending_dependent_count ?? 0}
              </dd>
            </div>
            <div className="rounded-xl border border-gray-200 px-3 py-2">
              <dt className="text-[11px] text-gray-500">Within cap</dt>
              <dd className="text-lg font-semibold text-gray-900">
                {summary.year_end_al.within_cap_count ?? 0}
              </dd>
            </div>
            <div className="rounded-xl border border-gray-200 px-3 py-2">
              <dt className="text-[11px] text-gray-500">Forfeiture days</dt>
              <dd className="text-lg font-semibold text-gray-900">
                {summary.year_end_al.projected_forfeiture_days ?? 0}
              </dd>
            </div>
          </dl>
        </div>
      )}

      {/* Section B: Visual comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <LeaveComparisonChart members={members} />
        <LastLeaveSummary members={members} />
      </div>

      <WorkArrangementOverview members={members} />

      {/* Section C: Patterns & Flags */}
      <PatternsAndFlags members={members} />
    </div>
  );
}
